import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, FileText, ArrowRight, Package, ShieldCheck, Users, Clock, Lock, ExternalLink, ShoppingBag } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { products } from '../data/products';
import { ProductPlaceholder } from '../components/ProductPlaceholder';
import { FormSelect } from '../components/FormSelect';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getShopFrameworkUrl, getShopProductUrl } from '../data/shopUrls';
export function FrameworksPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    org: '',
    orgType: '',
    sector: '',
    purpose: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  // Get a few sample products to show "Framework Relevant"
  const frameworkProducts = products.slice(0, 3);
  return <>
      <PageHero label="Procurement" title="Approved Procurement Frameworks" subtitle="Chiltern Global is appointed to key public sector frameworks, providing compliant, efficient, and transparent routes to market for government and emergency services buyers." data-id="element-707" />

      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Frameworks'
    }]} data-id="element-708" />

      {/* How Frameworks Help Buyers - NEW */}
      <section className="bg-white py-20" data-id="element-709">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-710">
          <div className="max-w-3xl mb-12" data-id="element-711">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-712">
              How Frameworks Help Buyers
            </h2>
            <p className="text-lg text-steel-600 leading-relaxed" data-id="element-713">
              Procurement frameworks reduce the time, cost, and risk associated
              with public sector purchasing. By using pre-tendered agreements,
              buyers can access Chiltern Global's manufacturing capabilities
              with the assurance that due diligence, price benchmarking, and
              compliance checks have already been conducted.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-714">
            <div className="bg-slate-50 p-6 border border-steel-200 rounded-sm" data-id="element-715">
              <ShieldCheck className="w-8 h-8 text-cg-orange-600 mb-4" data-id="element-716" />
              <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-717">
                Compliant & Auditable
              </h3>
              <p className="text-sm text-steel-600" data-id="element-718">
                Fully compliant with OJEU/PCR 2015 regulations, ensuring a
                transparent audit trail for all procurement activities.
              </p>
            </div>
            <div className="bg-slate-50 p-6 border border-steel-200 rounded-sm" data-id="element-719">
              <Users className="w-8 h-8 text-cg-orange-600 mb-4" data-id="element-720" />
              <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-721">
                Pre-Agreed Terms
              </h3>
              <p className="text-sm text-steel-600" data-id="element-722">
                Standardised terms and conditions and benchmarked pricing
                structures protect buyers and ensure value for money.
              </p>
            </div>
            <div className="bg-slate-50 p-6 border border-steel-200 rounded-sm" data-id="element-723">
              <Clock className="w-8 h-8 text-cg-orange-600 mb-4" data-id="element-724" />
              <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-725">
                Speed to Market
              </h3>
              <p className="text-sm text-steel-600" data-id="element-726">
                Significantly reduced procurement timescales with options for
                direct award or simplified mini-competition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Cards - ENHANCED */}
      <section className="bg-slate-50 py-24 border-t border-steel-200" data-id="element-727">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-728">
          <h2 className="text-2xl font-bold text-navy-900 mb-10" data-id="element-729">
            Our Framework Appointments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20" data-id="element-730">
            {/* BlueLight */}
            <div className="bg-white p-10 border border-steel-200 border-t-4 border-t-cg-orange-600 shadow-sm flex flex-col" data-id="element-731">
              <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-732">
                BlueLight Commercial
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider mb-6" data-id="element-733">
                Emergency Services
              </span>

              <div className="space-y-6 mb-8 flex-1" data-id="element-734">
                <div data-id="element-735">
                  <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-736">
                    Who is this for?
                  </h4>
                  <p className="text-steel-600 text-sm" data-id="element-737">
                    Police forces, Fire & Rescue services, and Ambulance trusts
                    across the UK.
                  </p>
                </div>
                <div data-id="element-738">
                  <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-739">
                    What is covered?
                  </h4>
                  <p className="text-steel-600 text-sm" data-id="element-740">
                    Uniform, protective clothing, personal protective equipment
                    (PPE), and operational accessories.
                  </p>
                </div>
              </div>

              <a href="#request-framework-details" className="inline-flex items-center justify-center px-6 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-600 transition-colors w-full" data-id="element-741">
                Request Framework Details
              </a>
            </div>

            {/* YPO */}
            <div className="bg-white p-10 border border-steel-200 border-t-4 border-t-navy-900 shadow-sm flex flex-col" data-id="element-742">
              <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-743">
                YPO Framework
              </h3>
              <span className="text-xs font-bold text-navy-900 uppercase tracking-wider mb-6" data-id="element-744">
                Public Sector Wide
              </span>

              <div className="space-y-6 mb-8 flex-1" data-id="element-745">
                <div data-id="element-746">
                  <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-747">
                    Who is this for?
                  </h4>
                  <p className="text-steel-600 text-sm" data-id="element-748">
                    Local government, education, emergency services, and other
                    public sector bodies.
                  </p>
                </div>
                <div data-id="element-749">
                  <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-750">
                    What is covered?
                  </h4>
                  <p className="text-steel-600 text-sm" data-id="element-751">
                    Emergency response equipment, facilities management
                    supplies, and specialist clothing.
                  </p>
                </div>
              </div>

              <a href="#request-framework-details" className="inline-flex items-center justify-center px-6 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-600 transition-colors w-full" data-id="element-752">
                Request Framework Details
              </a>
            </div>
          </div>

          {/* Framework Relevant Categories */}
          <div data-id="element-753">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-754">
              <h3 className="text-lg font-bold text-navy-900 flex items-center gap-2" data-id="element-755">
                <Package className="w-5 h-5 text-cg-orange-600" data-id="element-756" />{' '}
                Framework-Aligned Categories
              </h3>
              <a href={getShopFrameworkUrl()} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-cg-orange-600 uppercase tracking-wider flex items-center gap-2 hover:text-navy-900 transition-colors" data-id="element-757">
                Browse framework-relevant categories in Inventory{' '}
                <ExternalLink className="w-4 h-4" data-id="element-758" />
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-759">
              {frameworkProducts.map(product => <a key={product.slug} href={getShopProductUrl(product.slug)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-white p-4 border border-steel-200 hover:border-cg-orange-600 transition-colors group" data-id="element-760">
                  <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-sm" data-id="element-761">
                    <ProductPlaceholder aspectRatio="1/1" className="w-full h-full" data-id="element-762" />
                  </div>
                  <div data-id="element-763">
                    <h4 className="text-sm font-bold text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-764">
                      {product.categoryName}
                    </h4>
                    <span className="text-xs text-steel-500 flex items-center gap-1" data-id="element-765">
                      View in Inventory <ExternalLink className="w-3 h-3" data-id="element-766" />
                    </span>
                  </div>
                </a>)}
            </div>
          </div>
        </div>
      </section>

      {/* Request Form - ENHANCED */}
      <section id="request-framework-details" className="bg-white py-24" data-id="element-767">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-768">
          <div className="text-center mb-12" data-id="element-769">
            <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-770">
              Request Framework Pack
            </h2>
            <p className="text-steel-600" data-id="element-771">
              Complete the form below to receive our comprehensive framework
              guide, including specific lot numbers, pricing structures, and
              buyer guides.
            </p>
          </div>

          {!submitted ? <form onSubmit={handleSubmit} className="space-y-6 bg-slate-50 p-8 border border-steel-200 rounded-sm" data-id="element-772">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-773">
                <div data-id="element-774">
                  <label htmlFor="firstName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-775">
                    First Name *
                  </label>
                  <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-776" />
                </div>
                <div data-id="element-777">
                  <label htmlFor="lastName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-778">
                    Last Name *
                  </label>
                  <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-779" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-780">
                <div data-id="element-781">
                  <label htmlFor="email" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-782">
                    Work Email *
                  </label>
                  <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-783" />
                </div>
                <div data-id="element-784">
                  <label htmlFor="telephone" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-785">
                    Telephone
                  </label>
                  <input type="tel" id="telephone" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.telephone} onChange={handleChange} data-id="element-786" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-787">
                <div data-id="element-788">
                  <label htmlFor="org" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-789">
                    Organisation *
                  </label>
                  <input type="text" id="org" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.org} onChange={handleChange} data-id="element-790" />
                </div>
                <div data-id="element-791">
                  <FormSelect id="orgType" label="Organisation Type" value={formData.orgType} onChange={handleChange} data-id="element-792">
                    <option value="" data-id="element-793">Select Type (Optional)</option>
                    <option value="Public sector buyer" data-id="element-794">
                      Public sector buyer
                    </option>
                    <option value="Defence buyer" data-id="element-795">Defence buyer</option>
                    <option value="Police force" data-id="element-796">Police force</option>
                    <option value="Custodial/prison service" data-id="element-797">
                      Custodial/prison service
                    </option>
                    <option value="Medical/NGO procurement" data-id="element-798">
                      Medical/NGO procurement
                    </option>
                    <option value="Private sector" data-id="element-799">Private sector</option>
                    <option value="Other" data-id="element-800">Other</option>
                  </FormSelect>
                </div>
              </div>

              <div data-id="element-801">
                <FormSelect id="sector" label="Sector" required value={formData.sector} onChange={handleChange} data-id="element-802">
                  <option value="" data-id="element-803">Select Sector</option>
                  <option value="Defence" data-id="element-804">Defence</option>
                  <option value="Police" data-id="element-805">Police</option>
                  <option value="Prisons" data-id="element-806">Prisons</option>
                  <option value="Medical" data-id="element-807">Medical</option>
                  <option value="Other" data-id="element-808">Other Public Sector</option>
                </FormSelect>
              </div>

              <div data-id="element-809">
                <label htmlFor="purpose" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-810">
                  Purpose of Request *
                </label>
                <textarea id="purpose" required rows={4} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" placeholder="e.g. Upcoming tender, market engagement, pricing enquiry, framework call-off..." value={formData.purpose} onChange={handleChange} data-id="element-811" />
              </div>

              <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors" data-id="element-812">
                Request Framework Pack
              </button>

              <div className="flex items-start gap-2 bg-white border border-steel-200 rounded-sm p-3" data-id="element-813">
                <Lock className="w-3.5 h-3.5 text-cg-orange-600 mt-0.5 flex-shrink-0" data-id="element-814" />
                <p className="text-xs text-steel-500 leading-relaxed" data-id="element-815">
                  This form is encrypted and all submissions are treated in
                  strict confidence. Your data is processed in accordance with
                  our{' '}
                  <Link to="/privacy-policy" className="underline hover:text-cg-orange-600" data-id="element-816">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms-of-service" className="underline hover:text-cg-orange-600" data-id="element-817">
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </form> : <div className="bg-green-50 border border-green-200 p-12 rounded-sm text-center" data-id="element-818">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" data-id="element-819" />
              <h3 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-820">
                Request Received
              </h3>
              <p className="text-steel-700 mb-8" data-id="element-821">
                Thank you. Your request for the framework pack has been logged.
                A member of our frameworks team will review your submission and
                send the relevant documentation to your email address.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-822">
                <Link to="/products" className="px-6 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-navy-800 transition-colors" data-id="element-823">
                  View Products
                </Link>
                <button onClick={() => setSubmitted(false)} className="px-6 py-3 border border-navy-900 text-navy-900 font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-slate-50 transition-colors" data-id="element-824">
                  Submit Another Request
                </button>
              </div>
            </div>}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-900 py-20 border-t border-white/5" data-id="element-825">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-826">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-827">
            Discuss a Requirement
          </h2>
          <p className="text-steel-300 max-w-2xl mx-auto mb-10 text-lg" data-id="element-828">
            Need to discuss a specific project outside of a framework agreement?
            Our team is available to support direct procurement enquiries.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center px-10 py-4 border border-white/30 hover:bg-white/10 text-white font-bold uppercase tracking-wider rounded-sm transition-colors" data-id="element-829">
            Contact Us
          </Link>
        </div>
      </section>
    </>;
}